import React, { useState, useEffect } from "react";
import { Grid, Typography, Button, Box, CircularProgress } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import JumboCardQuick from "@jumbo/components/JumboCardQuick";
import { useTranslation } from "react-i18next";

const DashboardButtons = ({ buttons, syncStatus }) => {
  const { t } = useTranslation();
  const [elapsedTime, setElapsedTime] = useState(syncStatus?.elapsedTime || "");

  // Update elapsed time every second while running
  useEffect(() => {
    if (!syncStatus?.isRunning) {
      setElapsedTime(syncStatus?.elapsedTime || "");
      return;
    }

    // Update immediately
    setElapsedTime(syncStatus?.elapsedTime || "");

    // Update every second
    const interval = setInterval(() => {
      const startTime = parseInt(
        localStorage.getItem("oberonSyncStartTime") || "0",
        10,
      );
      if (startTime > 0) {
        const elapsed = Math.floor((Date.now() - startTime) / 1000);
        const minutes = Math.floor(elapsed / 60);
        const seconds = elapsed % 60;
        setElapsedTime(`${minutes}:${seconds.toString().padStart(2, "0")}`);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [syncStatus?.isRunning, syncStatus?.elapsedTime]);

  // Determine background color based on sync status
  const getStatusBackgroundColor = () => {
    if (syncStatus?.completed === "success") return "success.main";
    if (syncStatus?.completed === "error") return "error.main";
    return "primary.main";
  };

  // Show status box when running or just completed
  const showStatusBox = syncStatus?.isRunning || syncStatus?.completed;

  return (
    <>
      <Grid item xs={12} md={6} lg={4}>
        <JumboCardQuick
          title={
            <Typography variant="h2">
              {t("widgets.dashboardButtons.title")}
            </Typography>
          }
          headerSx={{ mb: 0, pb: 0 }}
          sx={{ height: "100%" }}
        >
          {/* Sync Status Display */}
          {showStatusBox && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                mb: 2,
                p: 1.5,
                borderRadius: 1,
                backgroundColor: getStatusBackgroundColor(),
                color: "white",
              }}
            >
              {syncStatus?.isRunning && (
                <CircularProgress size={20} sx={{ color: "inherit" }} />
              )}
              {syncStatus?.completed === "success" && (
                <CheckCircleIcon sx={{ fontSize: 20 }} />
              )}
              {syncStatus?.completed === "error" && (
                <ErrorIcon sx={{ fontSize: 20 }} />
              )}
              <Typography variant="body2" sx={{ flex: 1 }}>
                {syncStatus?.message || "Prebieha..."}
                {elapsedTime && ` (${elapsedTime})`}
              </Typography>
              {syncStatus?.percent && (
                <Typography variant="body2">{syncStatus.percent}%</Typography>
              )}
            </Box>
          )}

          <Grid container spacing={2} alignItems="flex-start">
            {buttons.map((button, idx) => {
              const nextButton = idx < buttons.length - 1 ? buttons[idx + 1] : null;
              const isGroupedWithNext = nextButton?.groupWithPrevious === true;
              const isGroupedWithPrevious = button.groupWithPrevious === true;
              const showLogo = button.logo && !isGroupedWithPrevious;
              
              // If this button has logo and next is grouped, show logo with buttons side by side below it
              if (showLogo && isGroupedWithNext) {
                return (
                  <React.Fragment key={`group-${idx}`}>
                    {/* OBERON column: Logo + both buttons side by side */}
                    <Grid item xs={12} md={6} lg={6}>
                      <Box sx={{ mb: 2 }}>
                        <Box
                          component="img"
                          src={button.logo}
                          alt={button.title || "button"}
                          sx={{
                            display: "block",
                            width: "100%",
                            height: "auto",
                            mb: 1,
                          }}
                        />
                        <Grid container spacing={1}>
                          <Grid item xs={6}>
                            <Button
                              fullWidth
                              variant="outlined"
                              color="primary"
                              onClick={button.onClick}
                              size="small"
                              disabled={
                                syncStatus?.isRunning &&
                                !syncStatus?.completed &&
                                button.disableOnSync
                              }
                            >
                              {button.title}
                            </Button>
                          </Grid>
                          <Grid item xs={6}>
                            <Button
                              fullWidth
                              variant="outlined"
                              color="primary"
                              onClick={nextButton.onClick}
                              size="small"
                              disabled={
                                syncStatus?.isRunning &&
                                !syncStatus?.completed &&
                                nextButton.disableOnSync
                              }
                            >
                              {nextButton.title}
                            </Button>
                          </Grid>
                        </Grid>
                      </Box>
                    </Grid>
                  </React.Fragment>
                );
              }
              
              // Skip if this is the second button in a group (already rendered above)
              if (isGroupedWithPrevious && idx > 0 && buttons[idx - 1]?.logo) {
                return null;
              }

              // Regular button (with or without logo)
              return (
                <Grid
                  key={button.title || idx}
                  item
                  xs={12}
                  md={6}
                  lg={6}
                >
                  <Box sx={{ mb: 2, display: "flex", flexDirection: "column", alignItems: "center" }}>
                    {showLogo && (
                      <Box
                        component="img"
                        src={button.logo}
                        alt={button.title || "button"}
                        sx={{
                          display: "block",
                          width: "100%",
                          height: "auto",
                          mb: 1,
                        }}
                      />
                    )}
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={button.onClick}
                      size="small"
                      disabled={
                        syncStatus?.isRunning &&
                        !syncStatus?.completed &&
                        button.disableOnSync
                      }
                    >
                      {button.title}
                    </Button>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </JumboCardQuick>
      </Grid>
    </>
  );
};

export default DashboardButtons;
